import { useMemo, useState } from 'react';
import { Check, Pencil, Pill, Plus, Trash2 } from 'lucide-react';
import { format, parseISO, subDays } from 'date-fns';
import type { Database } from '@/integrations/supabase/types';
import { getNhsReminderAction, getNhsRepeatPrescriptionAction } from '@/lib/nhs-links';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppDisclosureGlyph,
  AppEmptyState,
  AppPanelButton,
  AppText,
} from '../../../ui-foundation';
import { buildSummary } from '@/components/RecurrenceBuilder';
import { recordBelongsToHealthSubject, type HealthSubject } from '../../../ui-foundation/features/health';
import { buildPetCareTemplates, type PetCareTemplateKey } from '../../../ui-foundation/features/pets';
import { TYPE_ICONS, type HealthReminder } from './health-shared';

type HealthMedicationRow = Database['public']['Tables']['health_medications']['Row'];

type Props = {
  subjects: HealthSubject[];
  reminders: HealthReminder[];
  medications: HealthMedicationRow[];
  onAddReminder: (subject: HealthSubject) => void;
  onEditReminder: (reminder: HealthReminder) => void;
  onCompleteReminder: (reminder: HealthReminder) => void;
  onDeleteReminder: (reminderId: string) => void;
  onAddMedication: (subject: HealthSubject) => void;
  onEditMedication: (medication: HealthMedicationRow) => void;
  onDeleteMedication: (medication: HealthMedicationRow) => void;
  onOpenPetCareTemplate: (subject: HealthSubject, templateKey: PetCareTemplateKey) => void;
  onAddPetCareLog: (subject: HealthSubject) => void;
};

function formatReminderDate(reminder: HealthReminder) {
  if (!reminder.due_date) return 'No date';
  const dateLabel = format(parseISO(reminder.due_date), 'd MMM yyyy');
  const timeLabel = formatDueTime(reminder.due_time || null);
  return timeLabel ? `${dateLabel} at ${timeLabel}` : dateLabel;
}

function formatDueTime(value: string | null) {
  if (!value) return '';
  const [hours, minutes] = value.split(':');
  const parsedHours = Number(hours);
  const parsedMinutes = Number(minutes || '0');
  if (!Number.isFinite(parsedHours) || !Number.isFinite(parsedMinutes)) {
    return value;
  }
  const suffix = parsedHours >= 12 ? 'pm' : 'am';
  const hour12 = parsedHours % 12 || 12;
  return `${hour12}:${String(parsedMinutes).padStart(2, '0')}${suffix}`;
}

function formatLeadTime(reminder: HealthReminder) {
  if (typeof reminder.reminder_days_before !== 'number' || Number.isNaN(reminder.reminder_days_before)) {
    return '';
  }
  if (reminder.reminder_days_before <= 0) {
    return 'Reminder on the day';
  }
  return `Reminder ${reminder.reminder_days_before} day${reminder.reminder_days_before === 1 ? '' : 's'} before`;
}

function getRecurrenceLabel(reminder: HealthReminder) {
  if (reminder.recurrence_type === 'custom' && reminder.recurrence_interval && reminder.recurrence_unit) {
    return buildSummary({
      recurrence_type: 'custom',
      recurrence_interval: reminder.recurrence_interval,
      recurrence_unit: reminder.recurrence_unit as any,
      recurrence_day_of_week: reminder.recurrence_day_of_week || null,
      recurrence_week_of_month: reminder.recurrence_week_of_month || null,
      recurrence_month_day: reminder.recurrence_month_day || null,
    });
  }

  if (reminder.frequency_months) {
    return `Every ${reminder.frequency_months} months`;
  }

  return '';
}

function getRepeatBadge(dateStr: string | null) {
  if (!dateStr) return { text: 'No repeat date', classes: 'bg-muted text-muted-foreground' };
  const days = Math.floor((parseISO(dateStr).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
  if (days < 0) return { text: `${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''} overdue`, classes: 'bg-red-100 text-red-800' };
  if (days < 14) return { text: `${days} day${days !== 1 ? 's' : ''}`, classes: 'bg-amber-100 text-amber-800' };
  return { text: `${days} day${days !== 1 ? 's' : ''}`, classes: 'bg-green-100 text-green-800' };
}

function formatRepeatSummary(item: HealthMedicationRow) {
  if (!item.repeat_due_date) return 'Add a repeat date to generate the 7-day reminder automatically.';
  const requestDate = format(subDays(parseISO(item.repeat_due_date), 7), 'd MMM yyyy');
  return `Repeat due ${format(parseISO(item.repeat_due_date), 'd MMM yyyy')} · Adulting reminds on ${requestDate}`;
}

function medicationBelongsToSubject(subject: HealthSubject, medication: HealthMedicationRow) {
  return recordBelongsToHealthSubject(subject, {
    subjectType: medication.subject_type,
    subjectId: medication.subject_id,
    subjectName: medication.subject_name || medication.person_name,
  });
}

export default function HealthPeopleAndPetsSection({
  subjects,
  reminders,
  medications,
  onAddReminder,
  onEditReminder,
  onCompleteReminder,
  onDeleteReminder,
  onAddMedication,
  onEditMedication,
  onDeleteMedication,
  onOpenPetCareTemplate,
  onAddPetCareLog,
}: Props) {
  const [openKey, setOpenKey] = useState<string>(subjects[0]?.key || 'self');
  const [confirmReminderDeleteId, setConfirmReminderDeleteId] = useState<string | null>(null);
  const [confirmMedicationDeleteId, setConfirmMedicationDeleteId] = useState<string | null>(null);
  const nhsRepeatAction = useMemo(() => getNhsRepeatPrescriptionAction(), []);

  const subjectGroups = useMemo(() => subjects.map((subject) => ({
    subject,
    reminders: reminders.filter((item) => recordBelongsToHealthSubject(subject, {
      subjectType: item.subject_type,
      subjectId: item.subject_id,
      subjectName: item.subject_name,
    })),
    medications: medications.filter((item) => medicationBelongsToSubject(subject, item)),
  })), [subjects, reminders, medications]);

  if (subjectGroups.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {subjectGroups.map(({ subject, reminders: subjectReminders, medications: subjectMedications }) => {
        const isOpen = openKey === subject.key;
        const activeReminders = subject.subjectType === 'pet'
          ? subjectReminders.filter((item) => !item.completed_at)
          : subjectReminders;
        const petCareHistory = subject.subjectType === 'pet'
          ? subjectReminders
              .filter((item) => Boolean(item.completed_at))
              .sort((left, right) => {
                const leftKey = left.due_date || left.completed_at || left.created_at;
                const rightKey = right.due_date || right.completed_at || right.created_at;
                return rightKey.localeCompare(leftKey);
              })
          : [];
        const petCareTemplates = subject.subjectType === 'pet'
          ? buildPetCareTemplates(subject.displayName)
          : [];
        const summaryMeta = subject.subjectType === 'pet'
          ? `${activeReminders.length} reminder${activeReminders.length === 1 ? '' : 's'} · ${subjectMedications.length} medication${subjectMedications.length === 1 ? '' : 's'} · ${petCareHistory.length} care log${petCareHistory.length === 1 ? '' : 's'}`
          : `${subjectReminders.length} reminder${subjectReminders.length === 1 ? '' : 's'} · ${subjectMedications.length} medication${subjectMedications.length === 1 ? '' : 's'}`;
        return (
          <AppCard key={subject.key}>
            <AppPanelButton
              onClick={() => setOpenKey((current) => current === subject.key ? '' : subject.key)}
              className="justify-between gap-3 px-0 py-0 hover:border-transparent hover:bg-transparent"
              aria-expanded={isOpen}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <AppText fontSize="$md" fontWeight="700">
                    {subject.displayName}
                  </AppText>
                  <AppText fontSize="$xs" color="$colorMuted">
                    {subject.subtitle}
                  </AppText>
                </div>
                <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                  {summaryMeta}
                </AppText>
              </div>
              <div className="flex items-center gap-2">
                <AppText fontSize="$lg">{subject.iconHint}</AppText>
                <AppDisclosureGlyph open={isOpen} />
              </div>
            </AppPanelButton>

            {isOpen ? (
              <AppCardBody className="pt-4" gap="$md">
                <AppButtonRow>
                  <AppButton size="sm" onPress={() => onAddReminder(subject)}>
                    <Plus size={14} />
                    Add appointment reminder
                  </AppButton>
                  <AppButton size="sm" tone="outline" onPress={() => onAddMedication(subject)}>
                    <Pill size={14} />
                    Add medication
                  </AppButton>
                </AppButtonRow>

                {subject.subjectType === 'pet' ? (
                  <div className="space-y-3">
                    <AppText fontSize="$xs" fontWeight="700" color="$colorMuted">
                      Pet care templates
                    </AppText>
                    <AppText fontSize="$sm" color="$colorMuted">
                      Start common pet jobs from a calmer template instead of typing the same reminders from scratch.
                    </AppText>
                    <AppButtonRow>
                      {petCareTemplates.map((template) => (
                        <AppButton
                          key={template.key}
                          size="sm"
                          tone="outline"
                          onPress={() => onOpenPetCareTemplate(subject, template.key)}
                        >
                          {template.label}
                        </AppButton>
                      ))}
                      <AppButton size="sm" tone="outline" onPress={() => onAddPetCareLog(subject)}>
                        Log care
                      </AppButton>
                    </AppButtonRow>
                  </div>
                ) : null}

                <div className="space-y-3">
                  <AppText fontSize="$xs" fontWeight="700" color="$colorMuted">
                    Appointment reminders
                  </AppText>
                  {activeReminders.length === 0 ? (
                    <AppEmptyState
                      title={`No reminders for ${subject.displayName} yet`}
                      body="Add the next appointment, review, screening, test, or treatment reminder here."
                      action={(
                        <AppButton size="sm" onPress={() => onAddReminder(subject)}>
                          <Plus size={14} />
                          Add reminder
                        </AppButton>
                      )}
                    />
                  ) : (
                    <div className="space-y-3">
                      {activeReminders.map((reminder) => {
                        const recurrenceLabel = getRecurrenceLabel(reminder);
                        const leadTimeLabel = formatLeadTime(reminder);
                        const nhsAction = getNhsReminderAction(reminder.type);
                        return (
                          <AppCard key={reminder.id} backgroundColor="$background">
                            <AppCardBody gap="$sm">
                              <div className="flex items-start gap-3">
                                <span className="mt-0.5 text-2xl shrink-0">{TYPE_ICONS[reminder.type]}</span>
                                <div className="min-w-0 flex-1">
                                  <AppText fontSize="$sm" fontWeight="700">
                                    {reminder.title}
                                  </AppText>
                                  <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                                    {formatReminderDate(reminder)}
                                    {recurrenceLabel ? ` · ${recurrenceLabel}` : ''}
                                  </AppText>
                                  {leadTimeLabel ? (
                                    <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                                      {leadTimeLabel}
                                    </AppText>
                                  ) : null}
                                  {reminder.notes ? (
                                    <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                                      {reminder.notes}
                                    </AppText>
                                  ) : null}
                                </div>
                              </div>
                              <AppButtonRow>
                                {nhsAction ? (
                                  <a
                                    href={nhsAction.href}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground"
                                  >
                                    {nhsAction.label}
                                  </a>
                                ) : null}
                                <AppButton size="sm" tone="outline" onPress={() => onCompleteReminder(reminder)}>
                                  <Check size={12} />
                                  Mark done
                                </AppButton>
                                <AppButton size="sm" tone="outline" onPress={() => onEditReminder(reminder)}>
                                  <Pencil size={12} />
                                  Edit
                                </AppButton>
                                {confirmReminderDeleteId === reminder.id ? (
                                  <>
                                    <AppButton size="sm" tone="destructive" onPress={() => {
                                      onDeleteReminder(reminder.id);
                                      setConfirmReminderDeleteId(null);
                                    }}>
                                      Confirm
                                    </AppButton>
                                    <AppButton size="sm" tone="outline" onPress={() => setConfirmReminderDeleteId(null)}>
                                      Cancel
                                    </AppButton>
                                  </>
                                ) : (
                                  <AppButton size="sm" tone="outline" onPress={() => setConfirmReminderDeleteId(reminder.id)}>
                                    <Trash2 size={12} />
                                    Delete
                                  </AppButton>
                                )}
                              </AppButtonRow>
                            </AppCardBody>
                          </AppCard>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <AppText fontSize="$xs" fontWeight="700" color="$colorMuted">
                    Medication tracker
                  </AppText>
                  {subjectMedications.length === 0 ? (
                    <AppEmptyState
                      title={`No medications for ${subject.displayName} yet`}
                      body="Track medicines, dosage, repeat timing, and the next request date here."
                      action={(
                        <AppButton size="sm" onPress={() => onAddMedication(subject)}>
                          <Plus size={14} />
                          Add medication
                        </AppButton>
                      )}
                    />
                  ) : (
                    <div className="space-y-3">
                      {subjectMedications.map((medication) => {
                        const badge = getRepeatBadge(medication.repeat_due_date);
                        return (
                          <AppCard key={medication.id} backgroundColor="$background">
                            <AppCardBody gap="$sm">
                              <div className="flex items-start gap-3">
                                <div className="mt-0.5 rounded-full bg-primary/10 p-2 text-primary">
                                  <Pill size={16} />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <AppText fontSize="$sm" fontWeight="700">
                                      {medication.medication_name}
                                    </AppText>
                                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${badge.classes}`}>
                                      {badge.text}
                                    </span>
                                  </div>
                                  <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                                    {medication.dosage ? medication.dosage : 'No dosage saved'}
                                    {medication.frequency ? ` · ${medication.frequency}` : ''}
                                  </AppText>
                                  <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                                    {formatRepeatSummary(medication)}
                                  </AppText>
                                  {medication.notes ? (
                                    <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                                      {medication.notes}
                                    </AppText>
                                  ) : null}
                                </div>
                              </div>
                              <AppButtonRow>
                                <a
                                  href={nhsRepeatAction.href}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground"
                                >
                                  {nhsRepeatAction.label}
                                </a>
                                <AppButton size="sm" tone="outline" onPress={() => onEditMedication(medication)}>
                                  <Pencil size={12} />
                                  Edit
                                </AppButton>
                                {confirmMedicationDeleteId === medication.id ? (
                                  <>
                                    <AppButton size="sm" tone="destructive" onPress={() => {
                                      onDeleteMedication(medication);
                                      setConfirmMedicationDeleteId(null);
                                    }}>
                                      Confirm
                                    </AppButton>
                                    <AppButton size="sm" tone="outline" onPress={() => setConfirmMedicationDeleteId(null)}>
                                      Cancel
                                    </AppButton>
                                  </>
                                ) : (
                                  <AppButton size="sm" tone="outline" onPress={() => setConfirmMedicationDeleteId(medication.id)}>
                                    <Trash2 size={12} />
                                    Delete
                                  </AppButton>
                                )}
                              </AppButtonRow>
                            </AppCardBody>
                          </AppCard>
                        );
                      })}
                    </div>
                  )}
                </div>

                {subject.subjectType === 'pet' ? (
                  <div className="space-y-3">
                    <AppText fontSize="$xs" fontWeight="700" color="$colorMuted">
                      Care history
                    </AppText>
                    {petCareHistory.length === 0 ? (
                      <AppEmptyState
                        title={`No pet care history for ${subject.displayName} yet`}
                        body="Log vet visits, vaccinations, grooming, or cleaning here so the care history stays visible in Health."
                        action={(
                          <AppButton size="sm" onPress={() => onAddPetCareLog(subject)}>
                            <Plus size={14} />
                            Log care
                          </AppButton>
                        )}
                      />
                    ) : (
                      <div className="space-y-3">
                        {petCareHistory.map((reminder) => (
                          <AppCard key={reminder.id} backgroundColor="$background">
                            <AppCardBody gap="$sm">
                              <div className="flex items-start gap-3">
                                <span className="mt-0.5 text-2xl shrink-0">{TYPE_ICONS[reminder.type]}</span>
                                <div className="min-w-0 flex-1">
                                  <AppText fontSize="$sm" fontWeight="700">
                                    {reminder.title}
                                  </AppText>
                                  <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                                    Logged for {reminder.due_date ? format(parseISO(reminder.due_date), 'd MMM yyyy') : 'a saved date'}
                                  </AppText>
                                  {reminder.notes ? (
                                    <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                                      {reminder.notes}
                                    </AppText>
                                  ) : null}
                                </div>
                              </div>
                              <AppButtonRow>
                                <AppButton size="sm" tone="outline" onPress={() => onEditReminder(reminder)}>
                                  <Pencil size={12} />
                                  Edit
                                </AppButton>
                                {confirmReminderDeleteId === reminder.id ? (
                                  <>
                                    <AppButton
                                      size="sm"
                                      tone="destructive"
                                      onPress={() => {
                                        onDeleteReminder(reminder.id);
                                        setConfirmReminderDeleteId(null);
                                      }}
                                    >
                                      Confirm
                                    </AppButton>
                                    <AppButton size="sm" tone="outline" onPress={() => setConfirmReminderDeleteId(null)}>
                                      Cancel
                                    </AppButton>
                                  </>
                                ) : (
                                  <AppButton size="sm" tone="outline" onPress={() => setConfirmReminderDeleteId(reminder.id)}>
                                    <Trash2 size={12} />
                                    Delete
                                  </AppButton>
                                )}
                              </AppButtonRow>
                            </AppCardBody>
                          </AppCard>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}
              </AppCardBody>
            ) : null}
          </AppCard>
        );
      })}
    </div>
  );
}
